<script setup>
import { computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './Text/FormattedContent.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

const { content, contentAttributes, conversationId } = useMessageContext();

const store = useStore();

const getFollowUpById = useMapGetter('followUps/getFollowUpById');
const getFollowUps = useMapGetter('followUps/getFollowUps');

const followUpId = computed(() => contentAttributes.value?.followUpId);
const followUpEvent = computed(
  () => contentAttributes.value?.followUpEvent || 'reminder'
);

const followUp = computed(() =>
  getFollowUpById.value(conversationId.value, followUpId.value)
);
const status = computed(() => followUp.value?.status || 'pending');
const isPending = computed(() => status.value === 'pending');

const scheduledTime = computed(() => {
  const time =
    followUp.value?.scheduled_at ||
    contentAttributes.value?.followUpScheduledAt;
  return time ? messageTimestamp(time, 'LLL d, h:mm a') : '';
});

onMounted(() => {
  if (getFollowUps.value(conversationId.value).length === 0) {
    store.dispatch('followUps/get', { conversationId: conversationId.value });
  }
});

const markDone = () =>
  store.dispatch('followUps/complete', {
    conversationId: conversationId.value,
    id: followUpId.value,
  });
const cancel = () =>
  store.dispatch('followUps/cancel', {
    conversationId: conversationId.value,
    id: followUpId.value,
  });
</script>

<template>
  <BaseBubble
    class="px-4 py-3"
    data-bubble-name="follow-up"
    variant-class-override="bg-n-teal-3 text-n-teal-12"
    meta-color-override="text-n-teal-12/50"
  >
    <div class="flex flex-col gap-2">
      <FormattedContent v-if="content" :content="content" />
      <div class="flex flex-col gap-1.5 pt-2 border-t border-n-teal-12/15">
        <span class="flex items-center gap-1 text-xs opacity-80">
          <Icon icon="i-ph-alarm" class="size-3.5" />
          {{
            followUpEvent === 'reminder'
              ? $t('CONVERSATION.FOLLOW_UP.REMINDER_LABEL')
              : $t('CONVERSATION.FOLLOW_UP.SCHEDULED_LABEL', {
                  time: scheduledTime,
                })
          }}
        </span>
        <div v-if="isPending" class="flex items-center gap-3 text-xs">
          <button
            type="button"
            class="flex items-center gap-1 font-medium hover:underline"
            @click="markDone"
          >
            <Icon icon="i-ph-check-circle" class="size-3.5" />
            {{ $t('CONVERSATION.FOLLOW_UP.MARK_DONE') }}
          </button>
          <button
            type="button"
            class="flex items-center gap-1 font-medium opacity-80 hover:underline"
            @click="cancel"
          >
            <Icon icon="i-ph-x-circle" class="size-3.5" />
            {{ $t('CONVERSATION.FOLLOW_UP.CANCEL') }}
          </button>
        </div>
        <span v-else class="text-xs font-medium opacity-70">
          {{ $t(`CONVERSATION.FOLLOW_UP.STATUS.${status}`) }}
        </span>
      </div>
    </div>
  </BaseBubble>
</template>
